import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DailyStat, TAttributionModel } from '@feathr/blackbox';
import type { ITanStackTableProps } from '@feathr/components';
import { TanStackTable } from '@feathr/components';
import type { ListResponse } from '@feathr/rachis';

import { getDailyStatsColumns } from './DailyStatsColumns';
import type { TDailyStatsRow } from './DailyStatsTable.utils';
import { computeTotals, sortRows } from './DailyStatsTable.utils';

import * as styles from './DailyStatsTable.css';

type TSort = NonNullable<ITanStackTableProps<TDailyStatsRow>['sort']>;

const DEFAULT_PAGE_SIZE = 20;

interface IProps {
  stats: ListResponse<DailyStat>;
  isPending: boolean;
  showSpend?: boolean;
  showConversions?: boolean;
  attributionModel?: TAttributionModel;
}

function DailyStatsTable({
  stats,
  isPending,
  showSpend = true,
  showConversions = true,
  attributionModel = 'full',
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [sort, setSort] = useState<TSort>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  /*
   * TanStackTable is manual sort + manual pagination: it renders exactly the rows it's
   * given and never reorders or slices them. To preserve the pre-migration behavior of
   * sorting and paginating the already-fetched list client-side (no extra requests, per
   * #7226/#7227), we sort and slice here and hand the table the current page's rows plus
   * controlled sort/page state. The dataset is one row per day of the report range, so
   * this per-render work is cheap; `observer` re-renders when the stats list loads.
   */
  const rows: TDailyStatsRow[] = stats.models.map((stat) => stat.toJS());
  const totals = computeTotals(rows, attributionModel);
  const sortedRows = sortRows(rows, sort, attributionModel);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const clampedPage = Math.min(page, pageCount - 1);
  const pageRows = sortedRows.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize);

  const columns = getDailyStatsColumns(t, { attributionModel, showConversions, showSpend, totals });

  function handleSortChange(nextSort: TSort): void {
    setSort(nextSort);
    setPage(0);
  }

  function handlePageSizeChange(nextPageSize: number): void {
    setPageSize(nextPageSize);
    setPage(0);
  }

  return (
    <TanStackTable<TDailyStatsRow>
      className={styles.tableWrapper}
      columns={columns}
      idKey={'id'}
      isLoading={isPending}
      items={pageRows}
      noDataText={t('No Data')}
      onPageChange={setPage}
      onPageSizeChange={handlePageSizeChange}
      onSortChange={handleSortChange}
      page={clampedPage}
      pages={pageCount}
      pageSize={pageSize}
      sort={sort}
    />
  );
}

export default observer(DailyStatsTable);
